import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DetailedCoin } from '../types';
import { SetSeriesItem } from '../lib/albumData';
import './SetSeriesProgressBar.css';

export interface SetProgressInfo {
  set: SetSeriesItem;
  title: string;
  total: number;
  oldOwned: number;
  newOwned: number;
  oldPct: number;
  newPct: number;
  isComplete: boolean;
  isNewlyCompleted: boolean;
}

interface SetSeriesProgressBarProps {
  coin?: DetailedCoin;
  isNewFind?: boolean;
  sets: SetSeriesItem[];
  userAlbum: Record<string, number>;
  rollNewFindIds?: Set<string>;
  revealed?: boolean;
  isExiting?: boolean;
}

export function computeContributingSets(
  coin: DetailedCoin | undefined,
  isNewFind: boolean | undefined,
  sets: SetSeriesItem[],
  userAlbum: Record<string, number>,
  rollNewFindIds: Set<string> = new Set()
): SetProgressInfo[] {
  if (!coin || sets.length === 0) return [];

  const results: SetProgressInfo[] = [];

  for (const set of sets) {
    const isContained = set.coins.some(c => c.id === coin.id);
    if (!isContained) continue;

    // Filter circulation vs all coins (if set has circulation coins, track circulation coins)
    const circCoins = set.coins.filter(c => c.mintage > 100_000);
    const targetCoins = circCoins.length > 0 ? circCoins : set.coins;
    const total = targetCoins.length;
    if (total === 0) continue;

    let owned = 0;
    for (const c of targetCoins) {
      const isCurrentlyOwned = (userAlbum[c.id] || 0) > 0 || rollNewFindIds.has(c.id);
      if (isCurrentlyOwned) {
        owned++;
      }
    }

    const wasOwnedBefore = (userAlbum[coin.id] || 0) > 0 || rollNewFindIds.has(coin.id);
    const isContributingNewFind = Boolean(isNewFind) && !wasOwnedBefore;

    const oldOwned = owned;
    const newOwned = isContributingNewFind ? Math.min(total, owned + 1) : owned;
    const oldPct = Math.min(100, Math.round((oldOwned / total) * 100));
    const newPct = Math.min(100, Math.round((newOwned / total) * 100));

    const isComplete = newOwned >= total;
    const isNewlyCompleted = isContributingNewFind && isComplete && (oldOwned < total);

    // Only show if it is a new find that contributes to this set
    if (isContributingNewFind || isNewFind) {
      results.push({
        set,
        title: set.title,
        total,
        oldOwned,
        newOwned,
        oldPct,
        newPct,
        isComplete,
        isNewlyCompleted
      });
    }
  }

  return results;
}

interface SingleSetCardProps {
  info: SetProgressInfo;
  revealed: boolean;
}

const SingleSetCard: React.FC<SingleSetCardProps> = ({ info, revealed }) => {
  const [displayOwned, setDisplayOwned] = useState<number>(info.oldOwned);
  const [displayPct, setDisplayPct] = useState<number>(info.oldPct);
  const [isAdvancing, setIsAdvancing] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false);

  useEffect(() => {
    // Reset to old value on coin change / mount
    setDisplayOwned(info.oldOwned);
    setDisplayPct(info.oldPct);
    setIsAdvancing(false);
    setIsDone(false);

    if (!revealed) return;

    // Delay before starting the progress advance animation
    const delayTimer = setTimeout(() => {
      setIsAdvancing(true);
      setDisplayOwned(info.newOwned);
      setDisplayPct(info.newPct);

      // Flash ends when transition finishes
      const advanceDurationTimer = setTimeout(() => {
        setIsAdvancing(false);
        setIsDone(true);
      }, 700);

      return () => clearTimeout(advanceDurationTimer);
    }, 450);

    return () => clearTimeout(delayTimer);
  }, [revealed, info.oldOwned, info.newOwned, info.oldPct, info.newPct]);

  const isCompletedNow = isDone && info.isComplete;

  return (
    <div className={`set-progress-card ${isCompletedNow ? 'complete' : ''} ${isAdvancing ? 'advancing' : ''}`}>
      <div className="set-progress-header">
        <div className="set-progress-title-wrap">
          <span className="set-progress-badge">SET PROGRESS</span>
          <span className="set-progress-title">{info.title}</span>
        </div>
        <div className="set-progress-stats">
          <span className={`set-progress-numbers ${isAdvancing ? 'stat-flash' : ''}`}>
            <strong>{displayOwned}</strong> / {info.total}
          </span>
          <span className={`set-progress-pct ${isAdvancing ? 'stat-flash' : ''}`}>
            {isCompletedNow ? 'Completed!' : `${displayPct}%`}
          </span>
        </div>
      </div>

      <div className="set-progress-bar-track">
        <div
          className={`set-progress-bar-fill ${isCompletedNow ? 'complete' : ''} ${isAdvancing ? 'advancing-fill' : ''}`}
          style={{ width: `${displayPct}%` }}
        />
      </div>
    </div>
  );
};

export const SetSeriesProgressBar: React.FC<SetSeriesProgressBarProps> = ({
  coin,
  isNewFind,
  sets,
  userAlbum,
  rollNewFindIds = new Set(),
  revealed = true,
  isExiting = false
}) => {
  const contributingSets = useMemo(() => {
    return computeContributingSets(coin, isNewFind, sets, userAlbum, rollNewFindIds);
  }, [coin, isNewFind, sets, userAlbum, rollNewFindIds]);

  if (contributingSets.length === 0) return null;

  return (
    <div className={`set-progress-container ${revealed && !isExiting ? 'visible' : 'hidden'}`}>
      {contributingSets.map((info) => (
        <SingleSetCard
          key={info.set.id}
          info={info}
          revealed={revealed && !isExiting}
        />
      ))}
    </div>
  );
};

